"use client";
// Imports
import useStore from "@/lib/state";
import { Sun, CloudSun, Cloudy, CloudRain } from "lucide-react";
import { buttonStyles, allButtonStyles } from "@/lib/utils";
import { useCallback, useEffect } from "react";

// Filter Bar
export function DayFilterBar({ days }) {
  // Variables
  const { setDayFilter, selectedDay } = useStore();

  useEffect(() => {
    const syncSelectedDay = () => {
      const day = new URL(window.location.href).searchParams.get("day");
      if (!day || !days?.length) return;

      const selected = days.find((item) => item?.value?.slug == day);
      if (selected) {
        setDayFilter(selected.value);
      }
    };

    syncSelectedDay();
    window.addEventListener("popstate", syncSelectedDay);
    return () => window.removeEventListener("popstate", syncSelectedDay);
  }, [days, setDayFilter]);

  const selectDay = useCallback((day) => {
    // Keep the shareable URL and filter state in the same event. Updating the
    // URL in an effect caused the full event grid to render twice per click.
    setDayFilter(day);

    const url = new URL(window.location.href);
    if (url.searchParams.get("day") !== day.slug) {
      url.searchParams.set("day", day.slug);
      // The events are already in the browser. Bypass Next's wrapped History
      // method so changing this client-only filter does not request RSC data.
      History.prototype.pushState.call(window.history, {}, "", url);
    }
  }, [setDayFilter]);

  if (!days) return null;

  // Render
  return (
    <div className="flex items-start justify-between h-24 col-span-4 space-x-2 overflow-x-scroll remove-scrollbars">
      <button
        onClick={() => selectDay({ slug: "all-days", label: "All Events" })}
        className={allButtonStyles({
          status: selectedDay.slug == "all-days" ? "selected" : "unselected",
        })}
        type="submit"
      >
        All Events
      </button>
      {days?.length > 0 &&
        days.map((day, index) => (
          <div key={index} className="flex flex-col items-center gap-x-2">
            <button
              onClick={() => selectDay(day.value)}
              className={buttonStyles({
                status:
                  selectedDay.slug == day?.value?.slug
                    ? "selected"
                    : "unselected",
                hasForecast: day.forecast ? true : false,
              })}
              type="submit"
            >
              <span>{day.label.day}</span>
              <span className="whitespace-nowrap">{day.label.date}</span>
            </button>
            {day?.forecast && (
              <div className="flex items-center justify-center w-full gap-2 pt-2 font-bold transition-all duration-300 text-button">
                <WeatherIcon icon={day.forecast?.day?.condition?.text} />
                <span className="">
                  {parseInt(day.forecast?.day?.maxtemp_c)}°
                </span>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function WeatherIcon({ icon }) {
  const size = 25;

  switch (icon) {
    case "Sunny":
    case "Clear":
      return <Sun size={size} />;

    case "Partly cloudy":
    case "Overcast":
      return <CloudSun size={size} />;

    case "Cloudy":
      return <Cloudy size={size} />;

    case "Patchy rain possible":
    case "Light rain":
    case "Moderate rain at times":
    case "Moderate rain":
    case "Heavy rain at times":
    case "Heavy rain":
      return <CloudRain size={size} />;

    default:
      return <CloudSun size={size} />;
  }
}
